import { IconAngleRight, IconBuilding, IconUserCircle } from '@maia/icons';
import {
  ActionButton,
  ActionButtonLink,
  TableCell,
  Typography,
} from '@maia/ui';
import { Link, ReactNode } from '@tanstack/react-router';
import {
  ColumnDef,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { match } from 'ts-pattern';
import { Quotations } from './queryOptions';
import { formatCurrency } from '@maia/utils';
import { useState } from 'react';

type Quotation = Quotations['response'][number];
type Customer = Quotation['customer'];

const columns: ColumnDef<Quotation>[] = [
  {
    accessorKey: 'reference',
    header: 'Offerte nummer',
    cell: ({ row }) => {
      return match(row.depth)
        .with(0, () => (
          <TableCell.Link
            to="/quotations/$guid"
            params={{
              guid: row.original.guid,
            }}
          >
            {row.original.reference}
          </TableCell.Link>
        ))
        .otherwise(() => null);
    },
  },
  {
    accessorKey: 'customer',
    header: 'Klantnaam',
    enableSorting: false,
    cell: ({ row }) => {
      return match(row.depth)
        .with(0, () => (
          <TableCell.Activity
            icon={mapCustomerToIcon(row.original.customer)}
            title={row.original.customer.consumer.fullName}
          />
        ))
        .otherwise(() => null);
    },
  },
  {
    accessorKey: 'products',
    header: 'Productnaam',
    enableSorting: false,
    cell: ({ row }) => {
      return match({ canExpand: row.getCanExpand(), depth: row.depth })
        .with({ canExpand: true, depth: 0 }, () => (
          <TableCell.Dropdown
            title="Meerdere producten"
            onPress={() => row.toggleExpanded()}
          />
        ))
        .with({ canExpand: false, depth: 1 }, () => (
          <TableCell.DropdownProduct
            icon={row.original.products?.[0].icon}
            title={row.original.products?.[0].title}
            price={formatCurrency(
              row.original.products?.[0].price.premium_after_tax,
            )}
          />
        ))
        .otherwise(() => (
          <Typography variant="body-xs" weight="medium">
            {row.original.products?.[0].title}
          </Typography>
        ));
    },
  },

  {
    accessorKey: 'createdAt',
    header: 'Datum',
    cell: ({ row }) => {
      return match(row.depth)
        .with(0, () => <TableCell.Date date={row.original.createdAt} />)
        .otherwise(() => null);
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      return match(row.depth)
        .with(0, () => <TableCell.Tag {...row.original.status} />)
        .otherwise(() => null);
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      return match(row.depth)
        .with(0, () => (
          <div className="flex gap-x-12 items-center">
            <ActionButtonLink
              icon={IconAngleRight}
              size="small"
              to="/quotations/$guid"
              params={{ guid: row.original.guid }}
            />
          </div>
        ))
        .otherwise(() => null);
    },
  },
];

function mapCustomerToIcon(customer: Customer) {
  return match(customer.type)
    .returnType<ReactNode>()
    .with('consumer', () => <IconUserCircle className="size-24" />)
    .with('organization', () => <IconBuilding className="size-24" />)
    .exhaustive();
}

export function useQuotationsTable(data?: Quotations) {
  const [expanded, setExpanded] = useState<ExpandedState>({});

  return useReactTable({
    columns,
    data: data?.response ?? [],
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => {
      if (row.products.length > 1) {
        return row.products.map((product) => ({ ...row, products: [product] }));
      }

      return undefined;
    },
    rowCount: data?.response.length,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: data?.pagination.totalPages,
  });
}
